import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useProducts } from '../../hooks/useProducts';
import { addToCart, getCart } from '../../utils/cart';
import type { Product } from '../../types/product';
import { useSEO, generateWebPageSchema } from '../../utils/seo';
import WishlistButton from '../../components/base/WishlistButton';
import CartNotification from '../../components/feature/CartNotification';

type FilterType = 'ALL' | 'PERFUME' | 'HAIR OIL';
type SortType = 'featured' | 'price-low' | 'price-high' | 'name';

const renderStars = (rating: number) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  for (let i = 0; i < fullStars; i++) {
    stars.push(<i key={`full-${i}`} className="ri-star-fill text-[#2E7D32] text-xs"></i>);
  }
  if (hasHalf) {
    stars.push(<i key="half" className="ri-star-half-fill text-[#2E7D32] text-xs"></i>);
  }
  const remaining = 5 - fullStars - (hasHalf ? 1 : 0);
  for (let i = 0; i < remaining; i++) {
    stars.push(<i key={`empty-${i}`} className="ri-star-line text-gray-300 text-xs"></i>);
  }
  return stars;
};

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialCategory = searchParams.get('category') || '';
  
  // Map category param to filter type
  const getCategoryFilter = (cat: string): FilterType => {
    if (cat === 'perfume') return 'PERFUME';
    if (cat === 'hair-oil') return 'HAIR OIL';
    return 'ALL';
  };
  
  const [filter, setFilter] = useState<FilterType>(getCategoryFilter(initialCategory));
  const [sort, setSort] = useState<SortType>('featured');
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [notifiedItem, setNotifiedItem] = useState<import('../../utils/cart').CartItem | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const navigate = useNavigate();

  const { products, loading, error } = useProducts(
    filter === 'ALL' ? undefined : filter,
    searchQuery
  );

  useSEO({
    title: 'Shop Luxury Perfumes & Hair Oils | Daliah Secret Collection',
    description: 'Browse our complete collection of handcrafted luxury perfumes and nourishing hair oils. Premium natural ingredients, expertly blended fragrances, and sustainable beauty products. Free shipping on orders over $100.',
    keywords: 'luxury perfumes, hair oils, natural fragrances, handcrafted perfumes, premium cosmetics, organic hair care, Daliah Secret products',
    canonical: '/products',
    ogType: 'website',
    schema: generateWebPageSchema(
      'Shop Luxury Perfumes & Hair Oils',
      'Browse our complete collection of handcrafted luxury perfumes and nourishing hair oils',
      '/products'
    ),
  });

  useEffect(() => {
    const s = searchParams.get('search') || '';
    const c = searchParams.get('category') || '';
    if (s) setSearchQuery(s);
    if (c) setFilter(getCategoryFilter(c));
  }, [searchParams]);

  const sortedProducts = [...products].sort((a, b) => {
    switch (sort) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const handleAddToCart = (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if sold out
    if (product.stock_quantity === 0) {
      setToastMessage('Ce produit est épuisé');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    
    const result = addToCart(
      {
        id: parseInt(product.id),
        name: product.name,
        price: product.price,
        image: product.image,
      },
      1,
      product.stock_quantity
    );
    
    if (result.success && result.item) {
      setNotifiedItem(result.item);
    } else {
      setToastMessage(result.message || 'Erreur lors de l\'ajout au panier');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilter('ALL');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Green Accent Header Banner - same as Home */}
      <div className="bg-gradient-to-r from-[#143D28] to-[#2E7D32] text-white py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 text-sm sm:text-base">
            <i className="ri-gift-line text-lg"></i>
            <span className="font-medium">Découvrez l&apos;élégance de Daliah Secret</span>
            <span className="hidden sm:inline">•</span>
            <span className="hidden sm:inline">Livraison gratuite dès $200</span>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-24 right-4 z-50 px-6 py-4 rounded-lg shadow-lg bg-red-500 animate-slide-in">
          <div className="flex items-center gap-3 text-white">
            <i className="ri-error-warning-line text-xl"></i>
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Page Title */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-2">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 font-serif mb-2">
          Nos Produits
        </h1>
        <p className="text-base sm:text-lg text-gray-500 max-w-2xl">
          Découvrez notre collection complète de produits de beauté premium
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm sticky top-24">
              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Rechercher
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Nom du produit..."
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#C4A574] transition-colors"
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Catégorie
                </label>
                <div className="space-y-2">
                  {(['ALL', 'PERFUME', 'HAIR OIL'] as FilterType[]).map((f) => (
                    <label key={f} className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="category"
                        value={f}
                        checked={filter === f}
                        onChange={(e) => setFilter(e.target.value as FilterType)}
                        className="w-4 h-4 text-[#C4A574] border-gray-300 focus:ring-[#C4A574]"
                      />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-[#C4A574] transition-colors">
                        {f === 'ALL' ? 'ALL' : f === 'PERFUME' ? 'PERFUMES' : 'HAIR OILS'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Trier par
                </label>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortType)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#C4A574] transition-colors bg-white"
                >
                  <option value="featured">En vedette</option>
                  <option value="price-low">Prix: Bas à Élevé</option>
                  <option value="price-high">Prix: Élevé à Bas</option>
                  <option value="name">Nom: A-Z</option>
                </select>
              </div>

              {/* Reset Button */}
              <button
                onClick={handleClearSearch}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                Réinitialiser les filtres
              </button>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Sort Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
              <p className="text-sm text-gray-600">
                {sortedProducts.length} produit{sortedProducts.length !== 1 ? 's' : ''} trouvé{sortedProducts.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                    <div className="h-64 bg-gray-200 animate-pulse"></div>
                    <div className="p-4">
                      <div className="h-3 bg-gray-200 rounded animate-pulse mb-2"></div>
                      <div className="h-5 bg-gray-200 rounded animate-pulse mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3 mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="py-24 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-3xl text-red-500"></i>
                </div>
                <h3 className="text-xl font-serif text-gray-800 mb-2">Unable to load products</h3>
                <p className="text-sm text-gray-400 mb-6">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2.5 rounded-full bg-[#2E7D32] text-white text-xs font-medium hover:bg-[#1B5E20] transition-all cursor-pointer whitespace-nowrap"
                >
                  TRY AGAIN
                </button>
              </div>
            ) : sortedProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-product-shop>
                {sortedProducts.map((product) => {
                  const isSoldOut = product.stock_quantity === 0;
                  const isLowStock = product.stock_quantity > 0 && product.stock_quantity <= 5;
                  
                  return (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer"
                    >
                      <div className="relative h-64 sm:h-80 overflow-hidden bg-gray-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        {isSoldOut ? (
                          <span className="absolute top-4 left-4 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                            ÉPUISÉ
                          </span>
                        ) : isLowStock ? (
                          <span className="absolute top-4 left-4 px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                            STOCK LIMITÉ
                          </span>
                        ) : product.status && (
                          <span className="absolute top-4 left-4 px-3 py-1 bg-[#C4A574] text-white text-xs font-semibold rounded-full whitespace-nowrap">
                            {product.status}
                          </span>
                        )}
                        <WishlistButton
                          product={{
                            id: parseInt(product.id),
                            name: product.name,
                            price: product.price,
                            image: product.image,
                            category: product.category,
                          }}
                        />
                      </div>
                      <div className="p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 group-hover:text-[#C4A574] transition-colors truncate">
                              {product.name}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500 truncate">{product.category}</p>
                          </div>
                        </div>
                        
                        {/* Stock Info */}
                        <div className="mb-3">
                          {isSoldOut ? (
                            <span className="text-xs text-red-600 font-medium">Épuisé</span>
                          ) : isLowStock ? (
                            <span className="text-xs text-orange-600 font-medium">
                              Plus que {product.stock_quantity} en stock
                            </span>
                          ) : (
                            <span className="text-xs text-green-600 font-medium">
                              {product.stock_quantity} en stock
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xl sm:text-2xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
                          </div>
                          <button
                            onClick={(e) => handleAddToCart(e, product)}
                            disabled={isSoldOut}
                            className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full transition-colors ${
                              isSoldOut 
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                : 'bg-[#C4A574] text-white hover:bg-[#B39565] cursor-pointer'
                            }`}
                          >
                            <i className="ri-shopping-bag-line text-lg sm:text-xl"></i>
                          </button>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-16">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                  <i className="ri-search-line text-3xl sm:text-4xl text-gray-400"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  Aucun produit trouvé
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6">
                  Essayez d'ajuster vos filtres ou votre recherche
                </p>
                <button
                  onClick={handleClearSearch}
                  className="px-6 py-2 bg-[#C4A574] text-white font-medium rounded-lg hover:bg-[#B39565] transition-colors whitespace-nowrap text-sm sm:text-base"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification */}
      <CartNotification item={notifiedItem} onClose={() => setNotifiedItem(null)} />

      <Footer />
    </div>
  );
}
